import React, { ReactElement, useEffect, useMemo } from "react";

import { ConnectedProps, connect } from "react-redux";
import { AnyAction } from "redux";
import { ThunkDispatch } from "redux-thunk";

import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";

import { RootState } from "App/Store";

import { nonce } from "../../../App/App";
import dateTimeField from "../../../Common/Fields/DateTimeField";
import ErrorOverlay from "../../../Components/DataGridCustomComponents/ErrorOverlay";
import LoadingOverlay from "../../../Components/DataGridCustomComponents/LoadingOverlay";
import Pagination from "../../../Components/DataGridCustomComponents/Pagination";
import BatchIdColumn from "./BatchIdColumn";
import { executeSearch, fetchBatchOperationsAction } from "./batchOperationsService";

interface ISearchResultsTabProps {
  keyRoot: string;
}

const mapStateToProps = (state: RootState) => ({
  batchOperationsProps: state.batchOperations,
  error: state.batchOperations.error,
  initialized: state.batchOperations.initialized,
  isLoading: state.batchOperations.isLoading,
  links: state.batchOperations.links,
  profile: state.profile,
  searchResults: state.batchOperations.searchResult,
});

const connector = connect(mapStateToProps);

type TypesFromRedux = ConnectedProps<typeof connector>;

type ComponentProps = ISearchResultsTabProps & TypesFromRedux;

function BatchOperationsSearchResultsTable(props: ComponentProps): ReactElement {
  const { dispatch, error, initialized, isLoading, links, searchResults } = props;

  useEffect(() => {
    if (!initialized) {
      (dispatch as ThunkDispatch<any, undefined, AnyAction>)(fetchBatchOperationsAction());
    }
  }, [dispatch, initialized]);

  const searchColumns: GridColDef[] = useMemo(() => {
    return [
      {
        editable: false,
        field: "id",
        flex: 1,
        headerName: "Batch Name",
        renderCell: (params: GridRenderCellParams) => {
          return <BatchIdColumn batch={params.row} />;
        },
        sortable: false,
      },
      {
        editable: false,
        field: "createdDate",
        flex: 1,
        headerName: "Created",
        renderCell: (params: GridRenderCellParams) => {
          return dateTimeField.renderForDisplay(params.row.createdDate);
        },
        sortable: false,
      },
    ];
  }, []);

  function handleNextPage() {
    (dispatch as ThunkDispatch<any, undefined, AnyAction>)(fetchBatchOperationsAction(links?.next));
  }

  function handlePreviousPage() {
    (dispatch as ThunkDispatch<any, undefined, AnyAction>)(fetchBatchOperationsAction(links?.previous));
  }

  function handleRetry() {
    (dispatch as ThunkDispatch<any, undefined, AnyAction>)(executeSearch());
  }

  return (
    <DataGrid
      columnBuffer={0}
      columns={searchColumns}
      components={{
        ErrorOverlay: ErrorOverlay,
        LoadingOverlay: LoadingOverlay,
        Pagination: Pagination,
      }}
      componentsProps={{
        errorOverlay: { error, onRetry: handleRetry },
        pagination: { handleNextPage, handlePreviousPage, isLoading, links },
      }}
      disableColumnFilter
      disableColumnMenu
      disableColumnSelector
      disableSelectionOnClick
      error={error}
      loading={isLoading}
      localeText={{
        noRowsLabel: "No results found",
      }}
      nonce={nonce}
      pagination
      paginationMode="server"
      rows={searchResults}
      rowsPerPageOptions={[100]}
      scrollbarSize={17}
      sortingMode="server"
      sortingOrder={["desc", "asc"]}
    />
  );
}

export default connector(BatchOperationsSearchResultsTable);
